// third-party
import { createSlice } from "@reduxjs/toolkit";

// project imports
import axios from "axios"; // this does not include app headers
import axiosServices from "../../utils/axios";
import { dispatch } from "../index";
import { storage } from "../../utils/helpers/storage";
import { SET_SELECTED_CLIENT, SET_SELECTED_SITE } from "../actions";

// ----------------------------------------------------------------------

const initialState = {
  selectedSiteId: storage.getSite() ? JSON.parse(storage.getSite()) : null,
  selectedSite: null,
  sites: {},
  isSelectedSiteLoading: false,
  locusTargetsConfig: null,
  locusTargetsError: null,
  locusTargetsLoading: false,
  ingestionStats: null,
  ingestionStatsLoading: false,
  ingestionStatsError: null,
};

const slice = createSlice({
  name: "site",
  initialState,
  reducers: {
    // HAS ERROR
    hasError(state, action) {
      state.error = action.payload;
    },
    // SET SELECTED SITE
    setSelectedSite(state, action) {
      state.selectedSite = action.payload;
    },
    setSelectedSiteId(state, action) {
      if (action.payload) {
        storage.setSite(JSON.stringify(action.payload));
      } else {
        storage.setSite("");
      }
      state.selectedSiteId = action.payload;
    },
    updateSites(state, action) {
      state.sites[action.payload.id] = action.payload;
    },
    updateWarehouseSummary(state, action) {
      if (state.selectedSite) {
        state.selectedSite.timeCompleted = action.payload;
      }
    },
    setIsSelectedSiteLoading(state, action) {
      state.isSelectedSiteLoading = action.payload;
    },
    // LOCUS TARGETS CONFIG REDUCERS
    setLocusTargetsLoading(state, action) {
      state.locusTargetsLoading = action.payload;
    },
    setLocusTargetsConfig(state, action) {
      state.locusTargetsConfig = action.payload;
      state.locusTargetsError = null;
    },
    setLocusTargetsError(state, action) {
      state.locusTargetsError = action.payload;
    },
    // INGESTION STATS REDUCERS
    setIngestionStatsLoading(state, action) {
      state.ingestionStatsLoading = action.payload;
    },
    setIngestionStats(state, action) {
      state.ingestionStats = action.payload;
      state.ingestionStatsError = null;
    },
    setIngestionStatsError(state, action) {
      state.ingestionStatsError = action.payload;
    }
  },
});

// Reducer
export default slice.reducer;
/**
 * @param {LocusSite} site
 */
export function setSelectedSite(site) {
  return async () => {
    if (!site) {
      dispatch(slice.actions.setSelectedSiteId(null));
      dispatch(slice.actions.setSelectedSite(null));
      return;
    }

    try {
      dispatch({
        type: SET_SELECTED_SITE,
        payload: site.id,
      });
      dispatch(slice.actions.setSelectedSiteId(site.id));

      dispatch(slice.actions.setIsSelectedSiteLoading(true));
      const response = await axiosServices.get(
        `${process.env.REACT_APP_SERVICES_API}/services/site/${site.id}`,
      );

      if (response && response?.data) {
        dispatch(slice.actions.setSelectedSite(response.data));
        dispatch(slice.actions.updateSites(response.data));
        dispatch(slice.actions.setIsSelectedSiteLoading(false));
        
        try {
          const summary = await getWarehouseSummaryData(response.data);
          dispatch(slice.actions.updateWarehouseSummary(summary));
        } catch (error) {
          // Silently handle warehouse summary fetch error
        }
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

async function getWarehouseSummaryData(selectedSite) {
  try {
    const response = await axiosServices.get(
      `${process.env.REACT_APP_SERVICES_API}/services/warehouse/summary?site=${selectedSite?.id}`,
    );
    if (response.data) {
      return {
        robots: {
          count: response.data.robots?.count || 0,
        },
        chargers: {
          count: response.data.chargers?.count || 0,
        },
        devices: {
          count: response.data.devices?.count || 0,
        },
        totals: response.data.totals || null,
        maxExecDateTimeProcessed:
          response.data.maxExecDateTimeProcessed || "N/A",
      };
    }
  } catch (error) {
    throw error;
  }
}

export async function getSiteSyncTime(legacySiteId) {
  return await axiosServices.get(
    `${process.env.REACT_APP_SERVICES_API}/services/warehouse/sync/time?legacySiteId=${legacySiteId}`,
  );
}

export function getWarehouseSummary(selectedSite) {
  return async () => {
    try {
      const response = await axiosServices.get(
        `${process.env.REACT_APP_SERVICES_API}/services/warehouse/summary?site=${selectedSite?.id}`,
      );
      if (response.data) {
        dispatch(
          slice.actions.setSelectedSite({
            ...selectedSite,
            timeCompleted: {
              robots: {
                count: response.data.robots?.count || 0,
              },
              chargers: {
                count: response.data.chargers?.count || 0,
              },
              devices: {
                count: response.data.devices?.count || 0,
              },
              totals: response.data.totals || null,
              maxExecDateTimeProcessed:
                response.data.maxExecDateTimeProcessed || "N/A",
            },
          }),
        );
      }
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// LOCUS TARGETS CONFIG FUNCTIONS
export function getLocusTargetsConfig(siteId, filters = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.setLocusTargetsLoading(true));
    
    let params = {
      page: 1,
      pageSize: 20
    };
    
    if (siteId) {
      params.site = siteId;
    }
    
    // Add filter parameters as query params (required by API)
    // Send the actual value including "All" when selected
    if (filters.custOwner) {
      params.custOwner = filters.custOwner;
    }
    if (filters.floor) {
      params.floor = filters.floor;
    }
    if (filters.taskType) {
      params.taskType = filters.taskType;
    }
    if (filters.peakTargets !== undefined) {
      params.peakTargets = filters.peakTargets;
    }
    
    try {
      const response = await axiosServices.get(
        `${process.env.REACT_APP_API}/config/config/locus/target`,
        {
          params,
        },
      );
      
      // Take the first row as specified in requirements
      const locusTargetsData = response?.data && response?.data?.rows[0]
        ? response?.data?.rows[0]
        : null;
        
      dispatch(slice.actions.setLocusTargetsConfig(locusTargetsData));
      dispatch(slice.actions.setLocusTargetsLoading(false));
      
      return response.data;
    } catch (error) {
      dispatch(slice.actions.setLocusTargetsLoading(false));
      
      // Check for the specific 400 error with code 205 (site target does not exist)
      if (error?.response?.status === 400 && error?.response?.data?.code === 205) {
        // This is expected when no config exists yet - not an error
        dispatch(slice.actions.setLocusTargetsConfig(null));
        return { count: 0, rows: [] };
      }
      
      dispatch(slice.actions.setLocusTargetsError(error));
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function saveLocusTargetsConfig(data) {
  return async (dispatch) => {
    try {
      const response = await axiosServices.post(
        `${process.env.REACT_APP_API}/config/config/locus/target`,
        data,
      );
      
      // Don't auto-update the config - let the component maintain its current state
      return response.data;
    } catch (error) {
      dispatch(slice.actions.setLocusTargetsError(error));
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function updateLocusTargetsConfig(targetId, data) {
  return async (dispatch) => {
    try {
      const response = await axiosServices.put(
        `${process.env.REACT_APP_API}/config/config/locus/target/${targetId}`,
        data,
      );
      
      // Don't auto-update the config - let the component maintain its current state
      return response.data;
    } catch (error) {
      dispatch(slice.actions.setLocusTargetsError(error));
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// INGESTION STATS FUNCTIONS
export function getIngestionStats(siteId) {
  return async (dispatch) => {
    dispatch(slice.actions.setIngestionStatsLoading(true));
    
    try {
      const response = await axiosServices.get(
        `${process.env.REACT_APP_SERVICES_API}/services/warehouse/ingestion/stats?site=${siteId}`,
      );

      dispatch(slice.actions.setIngestionStats(response.data));
      dispatch(slice.actions.setIngestionStatsLoading(false));
      
      return response.data;
    } catch (error) {
      dispatch(slice.actions.setIngestionStatsLoading(false));
      dispatch(slice.actions.setIngestionStatsError(error));
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
